import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Wallet, Save, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function SchoolBudgetSetup() {
  const handleSave = () => {
    toast.success('Budget saved successfully');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Wallet className="w-7 h-7 text-[#1E4D35]" />
          Budget Setup — CDS Cycle 1 & 2 2028
        </h1>
        <p className="text-slate-500 mt-1">Enter your approved library book budget for each CDS cycle</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Budget Entry</CardTitle>
          <CardDescription>
            Enter your approved library book budget for each CDS cycle to enable live tracking during title selection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 border rounded-lg bg-emerald-50">
              <Label className="text-emerald-800 font-medium">CDS Cycle 1 2028 (Jan – May 2028)</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-lg">$</span>
                <Input defaultValue="3,000.00" className="font-mono" />
                <Badge className="bg-emerald-100 text-emerald-700">✓ Confirmed</Badge>
              </div>
              <p className="text-xs text-emerald-600 mt-2">Entered 1 Aug 2026 by Mrs Tan</p>
            </div>
            <div className="p-4 border rounded-lg">
              <Label className="text-slate-700 font-medium">CDS Cycle 2 2028 (Jun – Oct 2028)</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-lg">$</span>
                <Input defaultValue="0" className="font-mono" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Will allocate from next year's budget</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
            <strong>Note:</strong> Annual total $3,000 spans both cycles.
          </p>
        </CardContent>
      </Card>

      {/* Live Budget Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Live Budget Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-8 flex rounded-full overflow-hidden">
              <div className="bg-[#1A3C6E] h-full" style={{ width: '16.8%' }} title="Mandatory NF EL $504" />
              <div className="bg-[#2E5F8A] h-full" style={{ width: '17.6%' }} title="Mandatory MTL $528.20" />
              <div className="bg-[#4F8BC9] h-full" style={{ width: '3.9%' }} title="Optional Fiction $117.60" />
              <div className="bg-[#7AADE4] h-full" style={{ width: '2.2%' }} title="Add'l Qty $65.00" />
              <div className="bg-[#94A3B8] h-full" style={{ width: '54.4%' }} title="System costs $1,632.70" />
              <div className="bg-slate-200 h-full" style={{ width: '5.1%' }} title="Remaining $152.50" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#1A3C6E]" />
                <span>NF EL $504</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#2E5F8A]" />
                <span>MTL $528.20</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#4F8BC9]" />
                <span>Fiction $117.60</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#7AADE4]" />
                <span>Add'l $65.00</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#94A3B8]" />
                <span>System $1,632.70</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-slate-200" />
                <span>Remaining $152.50</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Unit prices shown include books + acquisition fees + processing + logistics (aggregated)
          </p>
        </CardContent>
      </Card>

      {/* Budget History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Budget History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <span className="font-medium">2024</span>
                <span className="text-sm text-slate-500 ml-4">$2,912.00 budget</span>
              </div>
              <span className="text-sm">$2,870.50 committed | $41.50 remaining</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <span className="font-medium">2025</span>
                <span className="text-sm text-slate-500 ml-4">$3,000.00 budget</span>
              </div>
              <span className="text-sm">$2,950.00 committed | $50.00 remaining</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <span className="font-medium">2026 (Pilot)</span>
                <span className="text-sm text-slate-500 ml-4">MOE direct — N/A</span>
              </div>
              <span className="text-sm text-slate-400">—</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div>
                <span className="font-medium">2028 C1</span>
                <span className="text-sm text-slate-500 ml-4">$3,000.00 budget</span>
              </div>
              <span className="text-sm text-emerald-700">$2,847.50 committed | $152.50 remaining</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button variant="outline" onClick={handleSave}>
          <Save className="w-4 h-4 mr-2" />
          Save Budget
        </Button>
        <Button variant="outline">
          <Mail className="w-4 h-4 mr-2" />
          Request Budget Increase from HOD
        </Button>
      </div>
    </div>
  );
}

